import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Chip, Paper, IconButton, Tooltip, Collapse,
  Select, MenuItem, TextField, Switch, FormControlLabel, Button,
  Drawer, Menu, Alert, ListSubheader, InputAdornment
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  HelpOutline as DecisionIcon, PlayArrow as ActionIcon,
  Notifications as NotifyIcon, Loop as LoopIcon, CallOutlined as CallIcon,
  Add as AddIcon, MoreVert as MenuIcon, Undo as UndoIcon, Redo as RedoIcon,
  ContentCopy as CopyIcon, Delete as DeleteIcon
} from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import NodeEditor from './ConditionEditor';

/**
 * v6 decision-tree renderer + editor (Steps B–D).
 *   B: read-only outline, sentence titles, YES/NO branches, Inputs panel
 *   C: Run Test trace overlay (taken path highlighted, untaken dimmed)
 *   D: "+" insertion, step drawer, card menu (edit/dup/move/disable/delete),
 *      drag-and-drop reorder, undo/redo, draft autosave, save-as-version
 */

let uidSeq = 0;
const uid = () => `x${(++uidSeq).toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const clone = o => JSON.parse(JSON.stringify(o));
const reid = s => {
  const c = clone(s);
  const walk = st => (st || []).forEach(x => { x.id = uid(); walk(x.yes); walk(x.no); walk(x.steps); });
  walk([c]); return c;
};

// ── Tree navigation ─────────────────────────────────────────────────────────
function locate(steps, id) {
  for (let i = 0; i < (steps || []).length; i++) {
    const s = steps[i];
    if (s.id === id) return { list: steps, index: i, step: s };
    for (const k of ['yes', 'no', 'steps']) {
      const r = locate(s[k], id);
      if (r) return r;
    }
  }
  return null;
}
const findStep = (steps, id) => locate(steps, id)?.step || null;
const entryOf = t => (t.flows || []).find(f => (f.type || 'entry') === 'entry') || t.flows?.[0];

const countSteps = steps => (steps || []).reduce((n, s) =>
  n + 1 + countSteps(s.yes) + countSteps(s.no) + countSteps(s.steps), 0);

function collectInputRefs(value, acc = new Set()) {
  if (value && typeof value === 'object' && !Array.isArray(value) && (value.input || value.param)) {
    acc.add(value.input || value.param); return acc;
  }
  // 'params.x' strings — how passthrough fields (price_policy, exits) wire
  // inputs on specs that came from section IR or hand-written JSON.
  if (typeof value === 'string' && value.startsWith('params.')) { acc.add(value.slice(7)); return acc; }
  if (Array.isArray(value)) { value.forEach(v => collectInputRefs(v, acc)); return acc; }
  if (value && typeof value === 'object') Object.values(value).forEach(v => collectInputRefs(v, acc));
  return acc;
}

const SPEC_LOC = 'spec fields';
const INPUT_LOC = n => `input '${n}'`;

function indexUsage(live, usage = {}) {
  const entry = entryOf(live);
  const walk = (steps) => {
    for (const s of steps || []) {
      // Only this card's own fields count — walking yes/no/steps here would
      // attribute every descendant ref to its ancestors as well.
      const own = {};
      for (const [k, v] of Object.entries(s)) {
        if (!['yes', 'no', 'steps'].includes(k)) own[k] = v;
      }
      const refs = collectInputRefs(own);
      if (s.type === 'decision.switch' && typeof s.input === 'string') refs.add(s.input);
      for (const r of refs) if (!usage[r]?.includes(s.id)) (usage[r] = usage[r] || []).push(s.id);
      walk(s.yes); walk(s.no); walk(s.steps);
    }
  };
  walk(entry?.steps);
  // Spec-level passthrough fields (price_policy, exits, circuit_breakers,
  // underlying…) compile into the send step's section spec — their refs are
  // real usage even though no card field mentions them.
  const specFields = {};
  for (const [k, v] of Object.entries(live || {})) {
    if (!['flows', 'inputs', 'strategy', 'name', 'version', 'description'].includes(k)) specFields[k] = v;
  }
  for (const r of collectInputRefs(specFields)) {
    if (!usage[r]?.includes(SPEC_LOC)) (usage[r] = usage[r] || []).push(SPEC_LOC);
  }
  // Input defaults may reference other inputs — exit_options carries the
  // profit-target/stop/time-exit values as fields or 'params.x' strings.
  for (const i of live?.inputs || []) {
    for (const r of collectInputRefs(i.default)) {
      if (!usage[r]?.includes(INPUT_LOC(i.name))) (usage[r] = usage[r] || []).push(INPUT_LOC(i.name));
    }
  }
  return usage;
}

/** Human summary of a usage list: "3 step(s) + spec fields + via exit options". */
function describeUsage(used) {
  const steps = used.filter(l => l !== SPEC_LOC && !String(l).startsWith('input '));
  const extras = used
    .filter(l => l === SPEC_LOC || String(l).startsWith('input '))
    .map(l => (l === SPEC_LOC ? SPEC_LOC
      : l === "input 'exit_options'" ? 'via exit options' : `via ${l}`));
  const parts = [];
  if (steps.length) parts.push(`${steps.length} step${steps.length === 1 ? '' : 's'}`);
  parts.push(...extras);
  return parts.join(' + ');
}

// WI-8 input groups — declared order is preserved inside each group.
const INPUT_GROUPS = [
  ['General', /^(symbol|open_new_positions|max_open_positions)$/],
  ['Filters', /vix|adx|rsi|ema|rv20|dist/i],
  ['Structure', /em_multiple|widen|width|min_credit|delta|strike|increment/i],
  ['Exits', /exit|profit_target|stop_|time_exit|_dte$/i],
  ['Costs', /slippage|cost|fee|commission/i],
];
const inputGroup = (i) =>
  INPUT_GROUPS.find(([, re]) => re.test(i.name))?.[0]
  || (i.type === 'switch' ? 'General' : 'Other');

const humanize = (n) => String(n).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ── "+" menu catalog (FR-E2) ────────────────────────────────────────────────
const STEP_MENU = [
  { group: 'Decisions', items: [
    { t: 'decision.switch', desc: 'Is a switch input on?', make: () => ({ type: 'decision.switch', input: '', yes: [], no: [] }) },
    { t: 'decision.can_open_new_position', desc: 'Bot enabled, not paused, under entry caps', make: () => ({ type: 'decision.can_open_new_position', yes: [], no: [] }) },
    { t: 'decision.position_count', desc: 'Open positions ≤ N', make: () => ({ type: 'decision.position_count', op: 'lte', value: 1, yes: [], no: [] }) },
    { t: 'decision.market', desc: 'Market metric vs range (VIX, symbol)', make: () => ({ type: 'decision.market', metric: 'vix', op: 'between', low: 0, high: 30, yes: [], no: [] }) },
    { t: 'decision.weekday', desc: 'Today is one of these days', make: () => ({ type: 'decision.weekday', days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'], yes: [], no: [] }) },
    { t: 'decision.conditions', desc: 'Condition group (indicators/filters)', make: () => ({ type: 'decision.conditions', op: 'all', conditions: [], yes: [], no: [] }) },
    { t: 'decision.check_list', desc: 'Several conditions, one shared NO', make: () => ({ type: 'decision.check_list', conditions: [{ id: 'check_1', label: 'condition 1', op: 'all', conditions: [], skip_reason: 'CHECK_FAIL' }], yes: [], no: [] }) },
  ]},
  { group: 'Actions', items: [
    { t: 'action.build_structure', desc: 'Choose template, expiry, strikes, width', make: () => ({ type: 'action.build_structure', config: { template: 'iron_condor', expiry: { dte: 7 } } }) },
    { t: 'action.send_entry_signal', desc: 'Open the built structure', make: () => ({ type: 'action.send_entry_signal', config: {} }) },
    { t: 'action.set_variable', desc: 'Store a value for later steps', make: () => ({ type: 'action.set_variable', name: 'var', value: 0 }) },
    { t: 'action.close_position', desc: 'Close an open position', make: () => ({ type: 'action.close_position' }) },
    { t: 'action.roll_position', desc: 'Roll an open position', make: () => ({ type: 'action.roll_position' }) },
  ]},
  { group: 'Notifications', items: [
    { t: 'notify', desc: 'Explain an outcome / record a reason', make: () => ({ type: 'notify', reason: 'NOTE', message: '' }) },
  ]},
  { group: 'Loops & Reuse', items: [
    { t: 'loop.for_each_symbol', desc: 'Run steps for each symbol', make: () => ({ type: 'loop.for_each_symbol', steps: [] }) },
    { t: 'action.call', desc: 'Run another flow in this strategy', make: () => ({ type: 'action.call', flow: '' }) },
  ]},
];

// ── Sentence titles (FR-E3/D1) ──────────────────────────────────────────────
const OP_WORDS = { lt: 'below', lte: 'at most', gt: 'above', gte: 'at least', eq: '=', neq: '≠', between: 'between' };

function Val({ v }) {
  if (v && typeof v === 'object' && !Array.isArray(v) && v.input) {
    return <Chip size="small" color="primary" variant="outlined" label={v.input}
      sx={{ height: 18, fontSize: 11, mx: 0.25, verticalAlign: 'baseline' }} />;
  }
  return <Box component="span" sx={{ fontWeight: 600 }}>{v == null ? '?' : typeof v === 'object' ? JSON.stringify(v) : String(v)}</Box>;
}

// ── Condition sentences — engine condition nodes rendered as text ──────────
const CMP_WORDS = {
  '<': 'below', '<=': 'at most', '>': 'above', '>=': 'at least',
  '==': '=', '!=': '≠', between: 'between',
  crosses_above: 'crosses above', crosses_below: 'crosses below',
  rising: 'rising for', falling: 'falling for',
};

function OperandText({ o }) {
  if (o == null) return <>?</>;
  if (typeof o !== 'object') return <Val v={o} />;
  if (o.input || o.param) return <Val v={{ input: o.input || o.param }} />;
  if (o.value !== undefined) return <Val v={o.value} />;
  if (o.context) return <strong>{o.context.replace(/_/g, ' ')}</strong>;
  if (o.indicator || o.priceField) {
    return <strong>{o.indicator || o.priceField}{o.offset ? `[${o.offset}]` : ''}</strong>;
  }
  if (o.arith) {
    return (
      <>
        ({(o.arith.operands || []).map((x, i) => (
          <React.Fragment key={i}>{i > 0 && ` ${o.arith.op} `}<OperandText o={x} /></React.Fragment>
        ))})
      </>
    );
  }
  return <>{fmtShort(o)}</>;
}

function CondLeaf({ c }) {
  const rhs = Array.isArray(c.rhs) ? c.rhs : (c.rhs != null ? [c.rhs] : []);
  return (
    <>
      <OperandText o={c.lhs} /> {CMP_WORDS[c.cmp] || c.cmp}{' '}
      {rhs.map((r, i) => (
        <React.Fragment key={i}>{i > 0 && ' and '}<OperandText o={r} /></React.Fragment>
      ))}
      {['rising', 'falling'].includes(c.cmp) && ' bars'}
    </>
  );
}

function CondGroup({ c }) {
  if (!c) return null;
  if (c.lhs || c.cmp) return <CondLeaf c={c} />;
  const kids = c.conditions || [];
  if (!kids.length) return <>no conditions defined</>;
  const joiner = c.op === 'any' ? ' or ' : ' and ';
  return (
    <>
      {kids.map((k, i) => (
        <React.Fragment key={i}>{i > 0 && joiner}<CondGroup c={k} /></React.Fragment>
      ))}
    </>
  );
}

function Sentence({ step }) {
  const t = step.type;
  const cfg = step.config || step;
  if (step.title) return <>{step.title}</>;
  switch (t) {
    case 'decision.switch': return <>Whether <Val v={{ input: step.input ?? cfg.input }} /> is turned on?</>;
    case 'decision.can_open_new_position': return <>Whether the bot can open a new position?</>;
    case 'decision.position_count': return <>Open positions with <Val v={step.symbol ?? cfg.symbol} /> are {OP_WORDS[step.op || 'lte'] || step.op} <Val v={step.value ?? cfg.value} /></>;
    case 'decision.market': {
      const m = step.metric ?? cfg.metric;
      return <>{m === 'vix' ? 'VIX' : <Val v={m} />} is {OP_WORDS[step.op || 'between'] || step.op} <Val v={step.low ?? cfg.low} />
        {(step.high ?? cfg.high) !== undefined && <> and <Val v={step.high ?? cfg.high} /></>}</>;
    }
    case 'decision.weekday': return <>Today is one of {(step.days ?? cfg.days ?? []).join(', ') || '—'}</>;
    case 'decision.check_list': return <>Check list — all {(step.conditions || []).length} conditions must pass</>;
    case 'decision.conditions': case 'decision.filter':
      return <><CondGroup c={step} />{step.no_reason ? ` (else ${step.no_reason})` : ''}</>;
    case 'action.build_structure': {
      const c = cfg;
      const bits = [<strong key="t">{c.template || 'structure'}</strong>];
      if (c.expiry?.dte != null || c.expiry?.dte?.input) bits.push(<span key="e"> at <Val v={c.expiry.dte} /> DTE</span>);
      else if (c._raw?.expiry?.selector) bits.push(<span key="e"> — expiry: <Box component="span" fontWeight={600}>{c._raw.expiry.selector}</Box></span>);
      if (c.shorts?.value != null) bits.push(<span key="s">, {c.shorts.method || 'delta'} <Val v={c.shorts.value} /> shorts</span>);
      if (c.wing_width != null || c.width != null) bits.push(<span key="w">, wings <Val v={c.wing_width ?? c.width} /></span>);
      else if (c.width_selection?.candidates) bits.push(<span key="w">, width {c.width_selection.policy || 'widest_fit'} of <Val v={c.width_selection.candidates} /></span>);
      return <Box component="span">{bits}</Box>;
    }
    case 'action.send_entry_signal': return <>Open position — send entry signal{(cfg.exit_options?.input) ? <> with <Val v={cfg.exit_options} /></> : ''}</>;
    case 'action.set_variable': return <>Set {step.name} = <Val v={step.value} /></>;
    case 'action.call': return <>Run '{step.flow}'</>;
    case 'action.wait': return <>Wait {JSON.stringify(step.until || step.duration || '')}</>;
    case 'action.close_position': return <>Close position</>;
    case 'action.roll_position': return <>Roll position</>;
    case 'loop.for_each_symbol': return <>For each symbol in {step.symbols ? <Val v={step.symbols} /> : 'the universe'}</>;
    case 'notify': return <>Notify{step.reason ? ` (${step.reason})` : ''}: {step.message || ''}</>;
    default: return <>{t}</>;
  }
}

const fmtShort = v => {
  const s = JSON.stringify(v);
  return s.length > 160 ? s.slice(0, 157) + '…' : s;
};

/** Plain-text rendering of a step's trace value — no raw JSON dumps. */
function fmtTraceValue(step, v) {
  if (v == null) return '';
  if (typeof v !== 'object') return `= ${v}`;
  if (step?.type === 'decision.can_open_new_position') {
    return `${v.enabled ? 'enabled' : 'DISABLED'} · ${v.paused ? 'paused' : 'not paused'}`
      + ` · entries today ${v.entries_today}/${v.max_entries_per_day}`;
  }
  if (step?.type === 'action.build_structure') {
    const bits = [v.template && `template ${v.template.replace(/_/g, ' ')}`];
    if (v.expiry) bits.push(`expiry ${v.expiry}`);
    return bits.filter(Boolean).join(' · ') || 'structure configured';
  }
  if (Array.isArray(v)) {
    // decision.conditions/filter — leaf results with observed values
    if (v.length && v[0]?.values) {
      const f = v.find(x => !x.pass);
      if (f) {
        const vals = f.values || {};
        const observed = vals.a ?? vals.aNow ?? (vals.vals || [])[0];
        return `failed: ${f.id || 'condition'}${observed !== undefined ? ` — observed ${fmtShort(observed)}` : ''}`;
      }
      return `all ${v.length} checks passed`;
    }
    return `${v.length} items`;
  }
  // Flat object of scalars → "k: v" pairs; anything deeper → truncated JSON.
  const flat = Object.entries(v).every(([, x]) => x == null || typeof x !== 'object');
  if (flat) return Object.entries(v).map(([k, x]) => `${k.replace(/_/g, ' ')} ${x}`).join(' · ');
  return fmtShort(v);
}

/** §5 sub-step line under an action card — expiry/strikes/price/width/send.
 * Failing sub-steps are red; the width sub-step expands the candidate table. */
function SubstepRow({ s }) {
  const failed = s.status === 'failed';
  const notReached = s.status === 'not_reached';
  const color = failed ? 'error.main' : notReached ? 'text.disabled' : 'text.secondary';
  const mark = notReached ? '·' : s.status === 'passed' ? '✓' : '✗';
  const sentence = (() => {
    switch (s.id) {
      case 'filters':
        return `filters — ${(s.conditions || []).map(c => `${c.id}:${c.result ? '✓' : '✗'}`).join(' ')}`;
      case 'expiry': {
        const v = s.value || {};
        return failed ? `expiry — ${s.reason}` : `expiry ${v.expiry} (${v.dte}d)${v.event ? ` · ${v.event}` : ''}${v.widened_by ? ` · widened ×${v.widened_by}` : ''}`;
      }
      case 'strikes': {
        const v = s.value || {};
        return failed ? `strikes — ${s.reason}` : `strikes ${v.short_put ?? '—'}P${v.short_call != null ? `/${v.short_call}C` : ''}${v.expected_move ? ` · EM ${v.expected_move} ×${v.em_multiple}` : ''}`;
      }
      case 'build':
        return `build ${s.value?.template || 'structure'}`;
      case 'price': {
        const v = s.value || {};
        return failed ? `price — ${s.reason || 'unavailable'}`
          : `net credit $${v.net_credit}${v.source === 'MODELED' ? ' (MODELED)' : ''}`;
      }
      case 'width':
        return failed ? `width — ${s.outcome || s.reason || 'no selection'}`
          : `width ${s.candidates?.find(c => c.verdict === 'SELECTED')?.width ?? ''} selected${s.fallback ? ' (fallback)' : ''}`;
      case 'send_entry':
        return notReached ? 'send entry — not reached' : `send entry — ${s.status}`;
      default:
        return `${s.id} — ${s.status}${s.reason ? ` · ${s.reason}` : ''}`;
    }
  })();
  return (
    <Box>
      <Typography variant="caption" display="block" sx={{ color, fontFamily: 'monospace', fontSize: 11 }}>
        {mark} {sentence}
      </Typography>
      {s.id === 'width' && Array.isArray(s.candidates) && s.candidates.length > 0 && (
        <Box sx={{ ml: 2, mt: 0.25, mb: 0.5 }}>
          {s.candidates.map(c => (
            <Typography key={c.width} variant="caption" display="block"
              sx={{ fontFamily: 'monospace', fontSize: 10.5,
                color: c.verdict === 'SELECTED' ? 'success.main'
                  : c.verdict === 'DATA_UNAVAILABLE' ? 'text.disabled' : 'error.main' }}>
              {c.width}w {c.legs ? `${c.legs} ` : ''}
              {c.net_credit != null ? `cr $${c.net_credit} (${c.credit_pct_of_width}%) ` : 'unpriced '}
              {c.max_loss_per_unit != null ? `maxLoss $${c.max_loss_per_unit} ` : ''}
              {c.contracts_sizable != null ? `fits ${c.contracts_sizable} ` : ''}
              → {c.verdict}
            </Typography>
          ))}
          {s.risk_context && (
            <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: 10 }}>
              risk ctx: {s.risk_context.account_tag || '—'} {s.risk_context.source || ''}
              {s.risk_context.per_trade_cap_usd != null ? ` cap $${Math.round(s.risk_context.per_trade_cap_usd)}` : ''}
              {s.risk_context.remaining_aggregate_usd != null ? ` agg $${Math.round(s.risk_context.remaining_aggregate_usd)}` : ''}
              {s.risk_context.error ? ` ${s.risk_context.error}` : ''}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

const TYPE_ICON = (t) => t?.startsWith('decision.') ? <DecisionIcon fontSize="small" color="info" />
  : t === 'notify' ? <NotifyIcon fontSize="small" color="warning" />
  : t === 'action.call' ? <CallIcon fontSize="small" />
  : t?.startsWith('loop.') ? <LoopIcon fontSize="small" />
  : <ActionIcon fontSize="small" color="success" />;

// ── Literal-or-Input field (FR-E4) ──────────────────────────────────────────
function ValueOrInput({ label, value, onChange, inputs, numeric = true }) {
  const isRef = value && typeof value === 'object' && value.input;
  const typed = (inputs || []).filter(i => numeric
    ? !['switch', 'exit_options', 'symbol', 'symbol_list', 'choice'].includes(i.type) : true);
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <Select size="small" value={isRef ? '__input__' : '__lit__'} sx={{ minWidth: 80 }}
        onChange={e => onChange(e.target.value === '__input__' ? { input: typed[0]?.name || '' } : (numeric ? 0 : ''))}>
        <MenuItem value="__lit__">literal</MenuItem>
        <MenuItem value="__input__">input</MenuItem>
      </Select>
      {isRef
        ? <Select size="small" value={value.input} sx={{ flex: 1 }}
            onChange={e => onChange({ input: e.target.value })}>
            {typed.map(i => <MenuItem key={i.name} value={i.name}>{i.name}</MenuItem>)}
          </Select>
        : <TextField size="small" label={label} type={numeric ? 'number' : 'text'} sx={{ flex: 1 }}
            value={value ?? ''} onChange={e => onChange(numeric ? +e.target.value : e.target.value)} />}
    </Box>
  );
}

// ── Step editor drawer (FR-E4) ──────────────────────────────────────────────
function StepDrawer({ step, inputs, flows, known, onChange, onClose }) {
  if (!step) return null;
  const set = (k, v) => onChange({ ...step, [k]: v });
  const setCfg = (k, v) => onChange({ ...step, config: { ...(step.config || {}), [k]: v } });
  const cfg = step.config || step;
  const field = (label, node) => label === null ? node
    : <Box sx={{ mb: 1.5 }}><Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{label}</Typography>{node}</Box>;
  const jsonField = (label, obj, onParse) => field(label,
    <TextField multiline minRows={4} fullWidth size="small" defaultValue={JSON.stringify(obj ?? null, null, 1)}
      onBlur={e => { try { onParse(JSON.parse(e.target.value)); } catch { /* keep */ } }} />);
  const isDecision = step.type?.startsWith('decision.');
  const wide = ['decision.conditions', 'decision.check_list', 'decision.filter'].includes(step.type);
  return (
    <Drawer anchor="right" open={!!step} onClose={onClose} PaperProps={{ sx: { width: wide ? 520 : 380, p: 2 } }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>{step.type}</Typography>
      <TextField size="small" fullWidth label="custom title (optional)" value={step.title || ''} sx={{ mb: 2 }}
        onChange={e => set('title', e.target.value || undefined)} />
      {step.type === 'decision.switch' && field('switch input',
        <Select size="small" fullWidth value={step.input || ''} onChange={e => set('input', e.target.value)}>
          {(inputs || []).filter(i => i.type === 'switch').map(i => <MenuItem key={i.name} value={i.name}>{i.name}</MenuItem>)}
        </Select>)}
      {step.type === 'decision.position_count' && (<>
        {field('symbol', <ValueOrInput value={step.symbol ?? cfg.symbol} inputs={inputs} numeric={false}
          onChange={v => set('symbol', v)} />)}
        {field('comparison', <Select size="small" fullWidth value={step.op || 'lte'} onChange={e => set('op', e.target.value)}>
          {Object.entries(OP_WORDS).filter(([k]) => k !== 'between').map(([k, w]) => <MenuItem key={k} value={k}>{k} — {w}</MenuItem>)}
        </Select>)}
        {field('value', <ValueOrInput value={step.value ?? cfg.value} inputs={inputs} onChange={v => set('value', v)} />)}
      </>)}
      {step.type === 'decision.market' && (<>
        {field('metric', <Select size="small" fullWidth value={step.metric || 'vix'} onChange={e => set('metric', e.target.value)}>
          <MenuItem value="vix">VIX</MenuItem></Select>)}
        {field('low', <ValueOrInput value={step.low ?? cfg.low} inputs={inputs} onChange={v => set('low', v)} />)}
        {field('high', <ValueOrInput value={step.high ?? cfg.high} inputs={inputs} onChange={v => set('high', v)} />)}
      </>)}
      {step.type === 'decision.weekday' && field('days',
        <Select size="small" fullWidth multiple value={step.days || []}
          onChange={e => set('days', e.target.value)}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </Select>)}
      {step.type === 'decision.check_list' && (<>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Check-list items — each box is a condition group; all must pass
        </Typography>
        {(step.conditions || []).map((item, i) => {
          const setItem = (next) => {
            const arr = [...step.conditions]; arr[i] = next; set('conditions', arr);
          };
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1, mb: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, alignItems: 'center' }}>
                <TextField size="small" label="label" value={item.label || item.id || ''} sx={{ width: 130 }}
                  onChange={e => setItem({ ...item, label: e.target.value })} />
                <TextField size="small" label="else reason" value={item.skip_reason || ''} sx={{ flex: 1 }}
                  onChange={e => setItem({ ...item, skip_reason: e.target.value || undefined })} />
                <IconButton size="small" onClick={() => set('conditions', step.conditions.filter((_, j) => j !== i))}>
                  <DeleteIcon fontSize="small" /></IconButton>
              </Box>
              <NodeEditor refKey="input" known={known}
                node={item.conditions
                  ? { op: item.op || 'all', conditions: item.conditions }
                  : { lhs: item.lhs, cmp: item.cmp, rhs: item.rhs }}
                onChange={n => setItem(n.op
                  ? { ...item, op: n.op, conditions: n.conditions, lhs: undefined, cmp: undefined, rhs: undefined }
                  : { ...item, lhs: n.lhs, cmp: n.cmp, rhs: n.rhs, op: undefined, conditions: undefined })}
                onRemove={() => setItem({ ...item, op: 'all', conditions: [], lhs: undefined, cmp: undefined, rhs: undefined })} />
            </Paper>
          );
        })}
        <Button size="small" startIcon={<AddIcon />} sx={{ mb: 1.5 }}
          onClick={() => set('conditions', [...(step.conditions || []), {
            id: `check_${(step.conditions || []).length + 1}`, label: '', op: 'all',
            conditions: [{ lhs: { context: 'vix_open' }, cmp: '<=', rhs: { value: 0 } }],
            skip_reason: 'CHECK_FAIL',
          }])}>
          Add condition
        </Button>
      </>)}
      {(step.type === 'decision.conditions' || step.type === 'decision.filter') && field('conditions',
        <NodeEditor refKey="input" known={known}
          node={step.conditions
            ? { op: step.op || 'all', conditions: step.conditions }
            : { lhs: step.lhs, cmp: step.cmp, rhs: step.rhs }}
          onChange={n => {
            if (n.op) onChange({ ...step, op: n.op, conditions: n.conditions, lhs: undefined, cmp: undefined, rhs: undefined });
            else onChange({ ...step, lhs: n.lhs, cmp: n.cmp, rhs: n.rhs, op: undefined, conditions: undefined });
          }}
          onRemove={() => onChange({ ...step, op: 'all', conditions: [], lhs: undefined, cmp: undefined, rhs: undefined })} />)}
      {step.type === 'action.build_structure' && (<>
        {field('template', <TextField size="small" fullWidth value={cfg.template || ''} onChange={e => setCfg('template', e.target.value)} />)}
        {field('expiry DTE', <ValueOrInput value={cfg.expiry?.dte} inputs={inputs} onChange={v => setCfg('expiry', { ...(cfg.expiry || {}), dte: v })} />)}
        {field('wing width', <ValueOrInput value={cfg.wing_width ?? cfg.width} inputs={inputs} onChange={v => setCfg('wing_width', v)} />)}
        {jsonField('full config (JSON)', cfg, v => set('config', v))}
      </>)}
      {step.type === 'action.send_entry_signal' && field('exit options',
        <Select size="small" fullWidth value={cfg.exit_options?.input || ''} displayEmpty
          onChange={e => setCfg('exit_options', e.target.value ? { input: e.target.value } : undefined)}>
          <MenuItem value="">(resolved at run time)</MenuItem>
          {(inputs || []).filter(i => i.type === 'exit_options').map(i => <MenuItem key={i.name} value={i.name}>{i.name}</MenuItem>)}
        </Select>)}
      {step.type === 'action.set_variable' && (<>
        {field('name', <TextField size="small" fullWidth value={step.name || ''} onChange={e => set('name', e.target.value)} />)}
        {field('value', <ValueOrInput value={step.value} inputs={inputs} numeric={false} onChange={v => set('value', v)} />)}
      </>)}
      {step.type === 'action.call' && field('flow',
        <Select size="small" fullWidth value={step.flow || ''} onChange={e => set('flow', e.target.value)}>
          {(flows || []).map(f => <MenuItem key={f.id} value={f.id}>{f.id}</MenuItem>)}
        </Select>)}
      {step.type === 'loop.for_each_symbol' && field('symbols (blank = universe)',
        <TextField size="small" fullWidth value={Array.isArray(step.symbols) ? step.symbols.join(', ') : ''}
          onChange={e => set('symbols', e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean))} />)}
      {step.type === 'notify' && (<>
        {field('reason code', <TextField size="small" fullWidth value={step.reason || ''} onChange={e => set('reason', e.target.value)} />)}
        {field('message', <TextField size="small" fullWidth multiline minRows={2} value={step.message || ''} onChange={e => set('message', e.target.value)} />)}
      </>)}
      {isDecision && step.type !== 'decision.conditions' && step.type !== 'decision.check_list' && field('NO branch reason code',
        <TextField size="small" fullWidth value={step.no_reason || ''}
          onChange={e => set('no_reason', e.target.value || undefined)} />)}
      <Button size="small" onClick={onClose}>Done</Button>
    </Drawer>
  );
}

// ── "+" insertion (FR-E2) ───────────────────────────────────────────────────
function InsertPoint({ onInsert, onPaste, hasClip }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Tooltip title="add step">
        <IconButton size="small" onClick={e => setAnchor(e.currentTarget)}
          sx={{ p: 0.25, opacity: 0.55, '&:hover': { opacity: 1 }, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          <AddIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {STEP_MENU.flatMap(g => [
          <ListSubheader key={g.group} sx={{ lineHeight: '28px' }}>{g.group}</ListSubheader>,
          ...g.items.map(it => (
            <MenuItem key={it.t} dense onClick={() => { setAnchor(null); onInsert({ id: uid(), ...it.make() }); }}>
              <Box><Typography variant="body2">{it.t}</Typography>
                <Typography variant="caption" color="text.secondary">{it.desc}</Typography></Box>
            </MenuItem>
          )),
        ])}
        {hasClip && (
          <MenuItem dense onClick={() => { setAnchor(null); onPaste(); }}>
            <Typography variant="body2">Paste copied step</Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

// ── Step card ───────────────────────────────────────────────────────────────
function StepCard({ step, collapsed, onToggle, traced, dimmed, highlighted, editing, onMenu, onDragStart, onItemChange }) {
  const isDecision = step.type?.startsWith('decision.');
  const hasKids = (step.yes?.length || step.no?.length || step.steps?.length);
  const border = traced
    ? (['no', 'skipped', 'failed'].includes(traced.result) ? '2px solid #e57373' : '2px solid #66bb6a')
    : '1px solid';
  return (
    <Paper variant="outlined" draggable={!!editing} data-step-id={step.id}
      onDragStart={e => { e.dataTransfer.setData('text/step-id', step.id); onDragStart?.(step.id); }}
      sx={{
        px: 1.25, py: 0.75, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1,
        width: 'fit-content', maxWidth: '100%', minWidth: 160,
        borderColor: traced ? undefined : 'divider', border,
        opacity: dimmed ? 0.35 : (step.disabled ? 0.5 : 1),
        bgcolor: highlighted ? 'action.selected' : 'background.paper',
        cursor: editing ? 'grab' : 'default', transition: 'opacity .15s, background .15s',
      }}>
      {hasKids
        ? <IconButton size="small" onClick={onToggle} sx={{ p: 0.25 }}>
            {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
          </IconButton>
        : <Box sx={{ width: 20 }} />}
      {TYPE_ICON(step.type)}
      <Box sx={{ flex: 1, minWidth: 0 }} onClick={editing ? () => onMenu(step, 'edit') : undefined}>
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
          <Sentence step={step} />
          {step.disabled && <Chip size="small" label="disabled" sx={{ ml: 0.5, height: 16, fontSize: 10 }} />}
        </Typography>
        {traced && ((traced.value !== undefined && traced.value !== null) || traced.reason)
          && step.type !== 'decision.check_list' && (
          <Typography variant="caption" color={traced.result === 'failed' ? 'error.main' : 'text.secondary'}
            display="block" sx={{ mt: 0.25 }}>
            → {traced.result}
            {traced.value !== undefined && traced.value !== null ? ` · ${fmtTraceValue(step, traced.value)}` : ''}
            {traced.reason ? ` · ${traced.reason}` : ''}
          </Typography>
        )}
        {Array.isArray(traced?.substeps) && traced.substeps.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            {traced.substeps.map(s => (
              <SubstepRow key={s.id} s={s} />
            ))}
          </Box>
        )}
        {collapsed && hasKids && (
          <Typography variant="caption" color="text.secondary" display="block">
            {step.yes !== undefined && `YES: ${countSteps(step.yes)} step${countSteps(step.yes) === 1 ? '' : 's'}`}
            {step.yes !== undefined && step.no !== undefined && ' · '}
            {step.no !== undefined && `NO: ${countSteps(step.no)} step${countSteps(step.no) === 1 ? '' : 's'}`}
            {step.steps !== undefined && `${countSteps(step.steps)} step${countSteps(step.steps) === 1 ? '' : 's'}`}
          </Typography>
        )}
        {step.type === 'decision.check_list' && !collapsed && (step.conditions || []).map((c, i) => {
          const r = Array.isArray(traced?.value)
            ? traced.value.find(x => x.label === (c.label || c.id)) : null;
          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <Typography variant="caption" color="text.secondary" display="block"
                  sx={{ mt: 0.5, fontWeight: 700, letterSpacing: 0.5 }}>
                  AND
                </Typography>
              )}
              <Paper variant="outlined" onClick={editing ? (e) => { e.stopPropagation(); onMenu(step, 'edit'); } : undefined}
                sx={{
                  px: 1, py: 0.5, mt: 0.5, display: 'flex', alignItems: 'center',
                  gap: 0.75, flexWrap: 'wrap', bgcolor: 'grey.50',
                  cursor: editing ? 'pointer' : 'default',
                }}>
                {r && (
                  <Box component="span" sx={{ color: r.pass ? 'success.main' : 'error.main', fontWeight: 700, fontSize: 12 }}>
                    {r.pass ? '✓' : '✗'}
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" display="block" sx={{ fontWeight: 500 }}>
                    <CondGroup c={c} />
                  </Typography>
                  {(c.label || c.skip_reason) && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {c.label || c.id}{c.skip_reason ? ` · else ${c.skip_reason}` : ''}
                    </Typography>
                  )}
                </Box>
                {r && !r.pass && (r.reason || r.values) && (
                  <Typography variant="caption" color="error.main">
                    {r.reason}
                    {r.values && (() => {
                      const obs = r.values.a ?? r.values.aNow ?? (r.values.vals || [])[0];
                      const bound = r.values.b ?? (r.values.lo !== undefined ? `[${r.values.lo}–${r.values.hi}]` : undefined);
                      return obs !== undefined
                        ? ` — observed ${fmtShort(obs)}${bound !== undefined ? ` vs ${fmtShort(bound)}` : ''}` : '';
                    })()}
                  </Typography>
                )}
                {editing && (
                  <IconButton size="small" sx={{ p: 0.25 }}
                    onClick={e => { e.stopPropagation(); onItemChange?.(step, i, null); }}>
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Paper>
            </React.Fragment>
          );
        })}
        {editing && step.type === 'decision.check_list' && !collapsed && (
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            sx={{ mt: 0.5, textTransform: 'none', fontSize: 11 }}
            onClick={e => {
              e.stopPropagation();
              onItemChange?.(step, -1, {
                id: `check_${(step.conditions || []).length + 1}`, label: '', op: 'all',
                conditions: [{ lhs: { context: 'vix_open' }, cmp: '<=', rhs: { value: 0 } }],
                skip_reason: 'CHECK_FAIL',
              });
            }}>
            condition
          </Button>
        )}
      </Box>
      {traced && isDecision && (
        <Chip size="small" label={traced.result === 'yes' ? 'YES' : 'NO'}
          color={traced.result === 'yes' ? 'success' : 'error'} sx={{ height: 20, fontSize: 11 }} />
      )}
      {traced && step.type === 'action.send_entry_signal' && traced.result === 'entered' && (
        <Chip size="small" label="ENTER" color="success" sx={{ height: 20, fontSize: 11 }} />
      )}
      {editing && <IconButton size="small" onClick={e => onMenu(step, e)}><MenuIcon fontSize="small" /></IconButton>}
    </Paper>
  );
}

/**
 * Recursive step list. Mutations run against the whole tree via `editTree(fn)`;
 * `getList(t)` returns THIS list's array inside the cloned tree — the entry
 * steps at the root, a branch array inside a decision, or a loop's steps.
 * `depth` drives the capped indent: past INDENT_CAP the outline stops growing
 * rightward and switches to a dashed rail, so no branch is pushed off-screen.
 */
const INDENT_CAP = 6;
function StepList({ steps, getList, collapsedSet, toggle, traceMap, highlightSet, parentDim,
    editing, editTree, onMenu, onItemChange, clip, dragId, setDragId, listId = '__root__', depth = 0 }) {
  const [dragOver, setDragOver] = useState(null);
  const insertAt = (idx, step) => editTree(t => getList(t).splice(idx, 0, step));

  const dropAt = (idx) => {
    const id = dragId; if (!id) return;
    editTree(t => {
      const steps = entryOf(t).steps;
      const src = locate(steps, id);
      const dst = getList(t);
      if (!src || !dst) return;
      const ownerId = listId !== '__root__' ? listId.split(':')[0] : null;
      if (ownerId && findStep([src.step], ownerId)) return; // can't drop into own subtree
      const [moved] = src.list.splice(src.index, 1);
      let at = idx;
      if (src.list === dst && src.index < idx) at = idx - 1;
      dst.splice(Math.max(0, at), 0, moved);
    });
  };
  const dropProps = (idx) => editing ? {
    onDragOver: e => { e.preventDefault(); setDragOver(idx); },
    onDragLeave: () => setDragOver(null),
    onDrop: e => { e.preventDefault(); setDragOver(null); dropAt(idx); },
  } : {};

  return (
    <Box>
      {editing && (
        <Box sx={{ height: 4, bgcolor: dragOver === 0 ? 'primary.light' : 'transparent', mb: 0.5, borderRadius: 1 }} {...dropProps(0)} />
      )}
      {steps.map((step, i) => {
        const traced = traceMap[step.id];
        const isDecision = step.type?.startsWith('decision.');
        const collapsed = collapsedSet.has(step.id);
        const yesDim = traced?.result === 'no';
        const noDim = traced?.result === 'yes';
        return (
          <Box key={step.id}>
            <StepCard step={step} collapsed={collapsed} onToggle={() => toggle(step.id)}
              traced={traced} dimmed={parentDim} highlighted={highlightSet.has(step.id)}
              editing={editing} onMenu={onMenu} onDragStart={setDragId} onItemChange={onItemChange} />
            <Collapse in={!collapsed}>
              {isDecision && (step.yes !== undefined || step.no !== undefined) && (
                <Box>
                  {['yes', 'no'].map(br => {
                    const brDim = br === 'yes' ? yesDim : noDim;
                    const deep = depth >= INDENT_CAP;
                    const getBr = (t) => {
                      const c = findStep(entryOf(t).steps, step.id);
                      if (!c[br]) c[br] = [];
                      return c[br];
                    };
                    const line = brDim ? 'grey.300' : (br === 'yes' ? 'success.main' : 'error.main');
                    // Single vertical spine: YES subtree renders first, then
                    // NO below it — never side-by-side.
                    return (
                      <Box key={br} sx={{ display: 'flex', opacity: brDim ? 0.5 : 1 }}>
                        <Box sx={{ width: deep ? 26 : 40, flexShrink: 0, position: 'relative' }}>
                          <Box sx={{
                            position: 'absolute', left: 7, top: 0, bottom: 0, width: 0,
                            borderLeft: deep ? '2px dashed' : '2px solid',
                            borderColor: line,
                          }} />
                          <Chip size="small" label={br.toUpperCase()}
                            color={brDim ? 'default' : (br === 'yes' ? 'success' : 'error')}
                            variant="outlined"
                            sx={{ height: 16, fontSize: 9, mt: 0.25, position: 'relative',
                              bgcolor: 'background.paper',
                              '& .MuiChip-label': { px: 0.5 } }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                          {depth === INDENT_CAP && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              depth {depth + 1}+ — indent capped
                            </Typography>
                          )}
                          <StepList steps={step[br] || []} getList={getBr} listId={`${step.id}:${br}`}
                            collapsedSet={collapsedSet} toggle={toggle} traceMap={traceMap} highlightSet={highlightSet}
                            parentDim={brDim} editing={editing} editTree={editTree} onMenu={onMenu} onItemChange={onItemChange} clip={clip}
                            dragId={dragId} setDragId={setDragId} depth={depth + 1} />
                          {editing && <InsertPoint hasClip={!!clip}
                            onInsert={s => editTree(t => getBr(t).push(s))}
                            onPaste={() => editTree(t => getBr(t).push(reid(clip)))} />}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {step.steps && step.type?.startsWith('loop.') && (
                <Box sx={{ display: 'flex' }}>
                  <Box sx={{ width: depth >= INDENT_CAP ? 26 : 40, flexShrink: 0, position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute', left: 7, top: 0, bottom: 0, width: 0,
                      borderLeft: depth >= INDENT_CAP ? '2px dashed' : '2px solid',
                      borderColor: 'divider',
                    }} />
                    <Chip size="small" label="each" variant="outlined"
                      sx={{ height: 16, fontSize: 9, mt: 0.25, position: 'relative',
                        bgcolor: 'background.paper', '& .MuiChip-label': { px: 0.5 } }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                    <StepList steps={step.steps} listId={`${step.id}:steps`}
                      getList={(t) => findStep(entryOf(t).steps, step.id).steps}
                      collapsedSet={collapsedSet} toggle={toggle} traceMap={traceMap} highlightSet={highlightSet}
                      parentDim={parentDim} editing={editing} editTree={editTree} onMenu={onMenu} onItemChange={onItemChange} clip={clip}
                      dragId={dragId} setDragId={setDragId} depth={depth + 1} />
                    {editing && <InsertPoint hasClip={!!clip}
                      onInsert={s => editTree(t => findStep(entryOf(t).steps, step.id).steps.push(s))}
                      onPaste={() => editTree(t => findStep(entryOf(t).steps, step.id).steps.push(reid(clip)))} />}
                  </Box>
                </Box>
              )}
            </Collapse>
            {editing && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ height: 4, flex: 1, bgcolor: dragOver === i + 1 ? 'primary.light' : 'transparent', my: 0.5, borderRadius: 1 }} {...dropProps(i + 1)} />
                <InsertPoint hasClip={!!clip} onInsert={s => insertAt(i + 1, s)} onPaste={() => insertAt(i + 1, reid(clip))} />
              </Box>
            )}
          </Box>
        );
      })}
      {editing && !steps.length && (
        <InsertPoint hasClip={!!clip} onInsert={s => insertAt(0, s)} onPaste={() => insertAt(0, reid(clip))} />
      )}
    </Box>
  );
}

// ── Inputs panel (FR-I1/I3) ─────────────────────────────────────────────────
const fmtInputVal = v => (v && typeof v === 'object' && v.input) ? v.input
  : v && typeof v === 'object' ? JSON.stringify(v) : String(v);
const fmtExitOpts = d => {
  if (!d || typeof d !== 'object') return String(d);
  const parts = Object.entries(d)
    .filter(([k]) => k !== '_raw_exits')
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${fmtInputVal(v)}`);
  if (d._raw_exits) parts.push('full exit set preserved');
  return parts.join(' · ');
};

function InputsPanel({ inputs, usage, instances, highlightInput, setHighlightInput, onPatched }) {
  const [instId, setInstId] = useState(instances[0]?.id ?? '');
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  const inst = instances.find(i => i.id === +instId);
  const instVals = { ...(inst?.input_values || {}), ...vals };
  const eff = (i) => instVals[i.name] !== undefined ? instVals[i.name] : i.default;

  const save = async (name, value) => {
    const next = { ...(inst?.input_values || {}), [name]: value };
    setSaving(true);
    const res = await apiRequest(`/api/strategies/instances/${inst.id}`, {
      method: 'PATCH', body: JSON.stringify({ input_values: next }),
    });
    setSaving(false);
    if (res.status === 200) { setVals({}); onPatched?.(res.data.instance); }
  };

  // WI-8: group inputs (General/Filters/Structure/Exits/Costs), friendly
  // label with the technical name as secondary text, scrollable panel with
  // a sticky header.
  const grouped = [];
  const seen = new Set();
  for (const [g] of INPUT_GROUPS) {
    const rows = (inputs || []).filter(i => inputGroup(i) === g && !seen.has(i.name));
    rows.forEach(i => seen.add(i.name));
    if (rows.length) grouped.push([g, rows]);
  }
  const rest = (inputs || []).filter(i => !seen.has(i.name));
  if (rest.length) grouped.push(['Other', rest]);

  return (
    <Paper variant="outlined" sx={{ width: 280, flexShrink: 0, alignSelf: 'flex-start',
      maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 1.5, pb: 0.5, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1,
        borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2">Inputs</Typography>
      </Box>
      <Box sx={{ p: 1.5, pt: 0.5, overflowY: 'auto' }}>
      {grouped.map(([g, rows]) => (
        <Box key={g}>
          <ListSubheader disableSticky sx={{ px: 0, py: 0.5, lineHeight: '22px', fontSize: 11,
            textTransform: 'uppercase', letterSpacing: 0.6 }}>{g}</ListSubheader>
      {rows.map(i => {
        const used = usage[i.name] || [];
        const v = eff(i);
        return (
          <Box key={i.name} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                title={i.description || i.name}>
                {i.label || humanize(i.name)}
                {i.unit && <Chip size="small" label={i.unit} variant="outlined" sx={{ height: 14, fontSize: 9, ml: 0.5 }} />}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  {i.name}
                </Typography>
              </Typography>
              <Chip size="small" label={i.type} variant="outlined" sx={{ height: 16, fontSize: 10 }} />
              <Tooltip title={used.length
                ? `used in ${describeUsage(used)} — click to highlight`
                : 'unused input — no step, spec field, or input default references it'}>
                <Chip size="small" clickable disabled={!used.length}
                  color={highlightInput === i.name ? 'primary' : (used.length ? 'default' : 'warning')}
                  label={`${used.length} usage${used.length === 1 ? '' : 's'}`} sx={{ height: 16, fontSize: 10 }}
                  onClick={() => used.length && setHighlightInput(highlightInput === i.name ? null : i.name)} />
              </Tooltip>
            </Box>
            {inst && i.type === 'switch' && (
              <FormControlLabel sx={{ m: 0 }}
                control={<Switch size="small" checked={!!v}
                  onChange={e => { setVals(x => ({ ...x, [i.name]: e.target.checked })); save(i.name, e.target.checked); }} />}
                label={<Typography variant="caption">{v ? 'on' : 'off'}</Typography>} />
            )}
            {inst && ['number', 'percent', 'amount', 'delta', 'days', 'position_count'].includes(i.type) && (
              <TextField size="small" type="number" fullWidth
                value={v ?? ''}
                InputProps={i.unit ? {
                  endAdornment: <InputAdornment position="end">{i.unit}</InputAdornment>,
                } : undefined}
                inputProps={{ min: i.min, max: i.max }}
                onChange={e => setVals(x => ({ ...x, [i.name]: e.target.value === '' ? undefined : +e.target.value }))}
                onBlur={() => vals[i.name] !== undefined && save(i.name, vals[i.name])}
                onKeyDown={e => e.key === 'Enter' && vals[i.name] !== undefined && save(i.name, vals[i.name])} />
            )}
            {inst && (i.type === 'symbol' || i.type === 'choice' || i.type === 'time') && (
              <TextField size="small" fullWidth value={v ?? ''}
                onChange={e => setVals(x => ({ ...x, [i.name]: e.target.value }))}
                onBlur={() => vals[i.name] !== undefined && save(i.name, vals[i.name])}
                onKeyDown={e => e.key === 'Enter' && vals[i.name] !== undefined && save(i.name, vals[i.name])} />
            )}
            {inst && i.type === 'exit_options' && v && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {['profit_target_pct', 'stop_multiple_of_credit', 'time_exit_dte'].map(k => (
                  <TextField key={k} size="small" type="number" label={k.replace(/_/g, ' ')}
                    sx={{ width: 110 }} value={v[k] ?? ''}
                    onChange={e => setVals(x => ({
                      ...x, [i.name]: { ...v, [k]: e.target.value === '' ? undefined : +e.target.value },
                    }))}
                    onBlur={() => vals[i.name] && save(i.name, vals[i.name])} />
                ))}
              </Box>
            )}
            {!inst && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
                default: {i.type === 'exit_options' ? fmtExitOpts(i.default) : fmtInputVal(i.default)}
              </Typography>
            )}
            {inst?.input_values?.[i.name] !== undefined && (
              <Typography variant="caption" color="primary"> · overridden</Typography>
            )}
          </Box>
        );
      })}
        </Box>
      ))}
      {instances.length > 1 && (
        <Select size="small" fullWidth value={instId} onChange={e => { setInstId(e.target.value); setVals({}); }} sx={{ mt: 1 }}>
          {instances.map(i => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
        </Select>
      )}
      {saving && <Typography variant="caption" color="text.secondary">saving…</Typography>}
      </Box>
    </Paper>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function StrategyTreeView({ tree, instances = [], trace = null, result = null,
    onInstancePatched, onSaveVersion, strategyId }) {
  const [collapsedSet, setCollapsedSet] = useState(new Set());
  const [highlightInput, setHighlightInput] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [history, setHistory] = useState({ back: [], fwd: [] });
  const [drawerStep, setDrawerStep] = useState(null);
  const [menuState, setMenuState] = useState(null);
  const [clip, setClip] = useState(null);
  const [problems, setProblems] = useState(null);
  const [dragId, setDragId] = useState(null);

  const live = draft || tree;
  const entry = entryOf(live);
  const draftKey = strategyId ? `treeDraft:${strategyId}` : null;

  // Autosave drafts (FR-E9)
  useEffect(() => {
    if (draft && draftKey) localStorage.setItem(draftKey, JSON.stringify(draft));
  }, [draft, draftKey]);
  useEffect(() => {
    if (draftKey) {
      const saved = localStorage.getItem(draftKey);
      if (saved) { try { setDraft(JSON.parse(saved)); setEditing(true); } catch { /* ignore */ } }
    }
  }, [draftKey]);

  const usage = useMemo(() => indexUsage(live), [live]);
  const highlightSet = useMemo(() => new Set(usage[highlightInput] || []), [usage, highlightInput]);
  const traceMap = useMemo(() => {
    const m = {};
    for (const t of trace || []) if (t.step_id) m[t.step_id] = t;
    return m;
  }, [trace]);
  const allParents = useMemo(() => {
    const ids = [];
    const walk = (steps) => (steps || []).forEach(s => {
      if (s.yes?.length || s.no?.length || s.steps?.length) ids.push(s.id);
      walk(s.yes); walk(s.no); walk(s.steps);
    });
    walk(entry?.steps);
    return ids;
  }, [entry]);

  const toggle = (id) => setCollapsedSet(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  // All edits go through here: clone live → fn(t) → push history + draft
  const editTree = useCallback((fn) => {
    const next = clone(live);
    fn(next);
    setHistory(h => ({ back: [...h.back.slice(-49), live], fwd: [] }));
    setDraft(next);
    setProblems(null);
  }, [live]);

  const undo = () => {
    if (!history.back.length) return;
    const prev = history.back[history.back.length - 1];
    setHistory(h => ({ back: h.back.slice(0, -1), fwd: [live, ...h.fwd] }));
    setDraft(prev);
  };
  const redo = () => {
    if (!history.fwd.length) return;
    const next = history.fwd[0];
    setHistory(h => ({ back: [...h.back, live], fwd: h.fwd.slice(1) }));
    setDraft(next);
  };

  const stepOp = {
    remove: (id) => editTree(t => { const l = locate(entryOf(t).steps, id); if (l) l.list.splice(l.index, 1); }),
    replace: (id, next) => editTree(t => { const l = locate(entryOf(t).steps, id); if (l) l.list[l.index] = next; }),
    move: (id, dir) => editTree(t => {
      const l = locate(entryOf(t).steps, id); if (!l) return;
      const j = l.index + dir;
      if (j < 0 || j >= l.list.length) return;
      [l.list[l.index], l.list[j]] = [l.list[j], l.list[l.index]];
    }),
    duplicate: (id) => editTree(t => {
      const l = locate(entryOf(t).steps, id); if (!l) return;
      l.list.splice(l.index + 1, 0, reid(l.step));
    }),
    moveTo: (id, targetId, branch) => editTree(t => {
      const steps = entryOf(t).steps;
      const l = locate(steps, id);
      const tgt = findStep(steps, targetId);
      if (!l || !tgt || id === targetId || findStep([l.step], targetId)) return;
      if (!tgt[branch]) tgt[branch] = [];
      const [moved] = l.list.splice(l.index, 1);
      tgt[branch].push(moved);
    }),
  };

  // Check-list item ops: i === -1 appends, null deletes, otherwise replaces.
  const onItemChange = (step, i, item) => {
    const conds = step.conditions || [];
    stepOp.replace(step.id, {
      ...step,
      conditions: i === -1 ? [...conds, item]
        : item == null ? conds.filter((_, j) => j !== i)
        : conds.map((c, j) => j === i ? item : c),
    });
  };

  // Operand pickers for the visual condition editor (shared with the section
  // builder — inputs take the place of params in tree specs).
  const known = useMemo(() => ({
    indicatorIds: (live.indicators || []).map(i => i.id),
    paramNames: (live.inputs || []).map(i => i.name),
    contextValues: ['weekday', 'today', 'dte', 'spot', 'vix_open', 'entry_credit',
      'structure_pnl', 'structure_loss', 'calendar_dte', 'em', 'em_multiple'],
  }), [live]);

  const onMenu = (step, evOrEdit) => {
    if (evOrEdit === 'edit') { setDrawerStep(step); return; }
    setMenuState({ anchor: evOrEdit.currentTarget, step });
  };

  const decisionTargets = useMemo(() => {
    const out = [];
    const walk = (steps) => (steps || []).forEach(s => {
      if (s.type?.startsWith('decision.')) {
        out.push({ id: s.id, branch: 'yes', step: s });
        out.push({ id: s.id, branch: 'no', step: s });
      }
      walk(s.yes); walk(s.no); walk(s.steps);
    });
    walk(entry?.steps);
    return out;
  }, [entry]);

  const validate = async () => {
    const res = await apiRequest('/api/strategies/validate', { method: 'POST', body: JSON.stringify({ spec: live }) });
    const errs = res.status === 200 ? (res.data.errors || []) : [{ error: res.data?.error || 'validation failed' }];
    setProblems(errs);
    return errs.length === 0;
  };

  const save = async () => {
    if (!(await validate())) return;
    onSaveVersion?.(live);
    if (draftKey) localStorage.removeItem(draftKey);
    setDraft(null); setHistory({ back: [], fwd: [] }); setEditing(false);
  };
  const discard = () => {
    if (draftKey) localStorage.removeItem(draftKey);
    setDraft(null); setHistory({ back: [], fwd: [] }); setEditing(false);
  };

  if (!live) return null;
  const dirty = !!draft;
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {entry?.trigger && (
          <Chip size="small" variant="outlined"
            label={`${entry.trigger.type === 'trigger.scheduled' ? 'Scheduled' : entry.trigger.type} ${(entry.trigger.days || []).join('/')} ${entry.trigger.time || ''} ${entry.trigger.tz || ''}`} />
        )}
        {result && !editing && (
          <Chip size="small" label={`${result.decision}${result.reason_code ? `:${result.reason_code}` : ''}`}
            color={result.decision === 'ENTER' ? 'success' : result.decision === 'SKIP' ? 'warning' : 'default'} />
        )}
        {dirty && <Chip size="small" color="warning" label="unsaved draft" />}
        <Box sx={{ flex: 1 }} />
        {!editing && <Button size="small" onClick={() => setCollapsedSet(new Set(allParents))}>Collapse all</Button>}
        {!editing && <Button size="small" onClick={() => setCollapsedSet(new Set())}>Expand all</Button>}
        {onSaveVersion && !editing && <Button size="small" variant="outlined" onClick={() => setEditing(true)}>Edit tree</Button>}
        {editing && (<>
          <IconButton size="small" disabled={!history.back.length} onClick={undo}><UndoIcon fontSize="small" /></IconButton>
          <IconButton size="small" disabled={!history.fwd.length} onClick={redo}><RedoIcon fontSize="small" /></IconButton>
          <Button size="small" onClick={validate}>Validate</Button>
          <Button size="small" variant="contained" onClick={save}>Save as new version</Button>
          <Button size="small" color="error" onClick={discard}>Discard</Button>
        </>)}
      </Box>
      {problems && (
        <Alert severity={problems.length ? 'error' : 'success'} sx={{ mb: 1 }} onClose={() => setProblems(null)}>
          {problems.length ? problems.map(e => e.error).join(' · ') : 'Tree valid — ready to save'}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <InputsPanel inputs={live.inputs || []} usage={usage} instances={instances}
          highlightInput={highlightInput} setHighlightInput={setHighlightInput} onPatched={onInstancePatched} />
        <Box sx={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          {!entry?.steps?.length && <Typography color="text.secondary">No steps in this flow.</Typography>}
          <StepList steps={entry?.steps || []} getList={(t) => entryOf(t).steps}
            collapsedSet={collapsedSet} toggle={toggle} traceMap={traceMap} highlightSet={highlightSet}
            editing={editing} editTree={editTree} onMenu={onMenu} onItemChange={onItemChange} clip={clip}
            dragId={dragId} setDragId={setDragId} />
        </Box>
      </Box>

      <StepDrawer step={drawerStep} inputs={live.inputs || []} flows={live.flows || []} known={known}
        onChange={s => { stepOp.replace(s.id, s); setDrawerStep(s); }}
        onClose={() => setDrawerStep(null)} />

      <Menu anchorEl={menuState?.anchor} open={!!menuState} onClose={() => setMenuState(null)}>
        <MenuItem dense onClick={() => { setMenuState(null); setDrawerStep(menuState.step); }}>Edit</MenuItem>
        <MenuItem dense onClick={() => { stepOp.duplicate(menuState.step.id); setMenuState(null); }}>Duplicate</MenuItem>
        <MenuItem dense onClick={() => { setClip(menuState.step); setMenuState(null); }}>
          <CopyIcon fontSize="small" sx={{ mr: 1 }} /> Copy</MenuItem>
        <MenuItem dense onClick={() => { stepOp.move(menuState.step.id, -1); setMenuState(null); }}>Move up</MenuItem>
        <MenuItem dense onClick={() => { stepOp.move(menuState.step.id, 1); setMenuState(null); }}>Move down</MenuItem>
        {menuState && decisionTargets.length > 0 && [
          <ListSubheader key="h" sx={{ lineHeight: '28px' }}>Move into branch…</ListSubheader>,
          ...decisionTargets.map(d => {
            // Can't move a step into itself or its own subtree.
            const invalid = menuState.step.id === d.id || !!findStep([menuState.step], d.id);
            return (
              <MenuItem key={d.id + d.branch} dense disabled={invalid}
                onClick={() => { stepOp.moveTo(menuState.step.id, d.id, d.branch); setMenuState(null); }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Chip size="small" label={d.branch.toUpperCase()}
                    color={d.branch === 'yes' ? 'success' : 'error'} variant="outlined"
                    sx={{ height: 16, fontSize: 9, flexShrink: 0, '& .MuiChip-label': { px: 0.5 } }} />
                  <Box component="span" sx={{
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
                    <Sentence step={d.step} />
                  </Box>
                </Box>
              </MenuItem>
            );
          }),
        ]}
        <MenuItem dense onClick={() => { stepOp.replace(menuState.step.id, { ...menuState.step, disabled: !menuState.step.disabled }); setMenuState(null); }}>
          {menuState?.step?.disabled ? 'Enable' : 'Disable'}</MenuItem>
        <MenuItem dense sx={{ color: 'error.main' }}
          onClick={() => {
            const n = countSteps([menuState.step]) - 1;
            if (n > 0 && !window.confirm(`Delete this step and its ${n} nested step(s)?`)) { setMenuState(null); return; }
            stepOp.remove(menuState.step.id); setMenuState(null);
          }}>Delete</MenuItem>
      </Menu>
    </Box>
  );
}
